import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, ShieldCheck, Sparkles, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import type { ProviderId } from '@/src/infrastructure/config/firebase';
import type { AuthResult } from '@/src/modules/auth/session';
import { BrandLogo } from '@/src/modules/components/BrandLogo';
import { AnimatedGradientBackground } from '@/src/modules/components/background/AnimatedGradientBackground';
import { CURRENT_POLICY_VERSION } from '@/src/infrastructure/config/policyVersion';
import { consumePendingCatalogSelection } from '@/src/modules/catalog/pendingSelection';

type Tab = 'login' | 'signup';

interface LoginProps {
  /** Retained for route compatibility; social login has no separate sign-up. */
  initialTab?: Tab;
}

const authHighlights: Array<[string, LucideIcon]> = [
  ['Secure sign-in', ShieldCheck],
  ['Fast publish', ArrowRight],
  ['Premium templates', Sparkles],
];

// ── Provider icons ───────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.8 15.71 17.58V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z" fill="#4285F4" />
      <path d="M12 23C14.97 23 17.46 22.02 19.28 20.34L15.71 17.58C14.73 18.24 13.48 18.64 12 18.64C9.13 18.64 6.7 16.7 5.84 14.11H2.17V16.96C3.99 20.58 7.7 23 12 23Z" fill="#34A853" />
      <path d="M5.84 14.11C5.62 13.45 5.5 12.74 5.5 12C5.5 11.26 5.62 10.55 5.84 9.89V7.04H2.17C1.42 8.53 1 10.22 1 12C1 13.78 1.42 15.47 2.17 16.96L5.84 14.11Z" fill="#FBBC05" />
      <path d="M12 5.38C13.62 5.38 15.06 5.93 16.21 7.02L19.35 3.88C17.45 2.1 14.97 1 12 1C7.7 1 3.99 3.42 2.17 7.04L5.84 9.89C6.7 7.3 9.13 5.38 12 5.38Z" fill="#EA4335" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.03 1.79-4.7 4.53-4.7 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07Z"
        fill="#1877F2"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 23 23" width="20" height="20" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}

// ── Provider button ──────────────────────────────────────────────────────────

function ProviderButton({
  icon,
  label,
  loading,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-2xl border border-outline-variant/60 bg-white px-5 text-sm font-bold text-on-surface shadow-sm transition-all hover:-translate-y-0.5 hover:bg-surface-lowest disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
    >
      {loading ? (
        <span className="h-5 w-5 rounded-full border-2 border-on-surface-variant border-t-transparent animate-spin" />
      ) : (
        icon
      )}
      {label}
    </button>
  );
}

export function isSafeReturnTo(path: unknown): path is string {
  if (typeof path !== 'string') return false;
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return false;
  }
  if (path.startsWith('/login')) {
    return false;
  }
  return true;
}

export function Login({ initialTab }: LoginProps) {
  const navigate = useNavigate();
  const { user, hydrated, loginWithGoogle, loginWithFacebook, loginWithMicrosoft } =
    useAuth();

  const [pending, setPending] = useState<ProviderId | null>(null);
  const [error, setError] = useState('');
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<{
    id: ProviderId;
    fn: () => Promise<AuthResult>;
  } | null>(null);
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const locationStateFrom = (location.state as { from?: string | { pathname?: string; search?: string; hash?: string } } | null)?.from;
  const returnTo = useMemo(() => {
    const fromParam = searchParams.get('returnTo');
    const candidate =
      fromParam ||
      (typeof locationStateFrom === 'string'
        ? locationStateFrom
        : locationStateFrom && typeof locationStateFrom === 'object' && typeof locationStateFrom.pathname === 'string'
          ? `${locationStateFrom.pathname}${locationStateFrom.search ?? ''}${locationStateFrom.hash ?? ''}`
          : null);
    return isSafeReturnTo(candidate) ? candidate : null;
  }, [searchParams, locationStateFrom]);

  // Guards the sessionStorage read below against StrictMode's double-invoked
  // mount effect in dev — without it, the first pass consumes (and removes)
  // the pending selection and navigates to /wizard, then the second pass
  // finds nothing and overwrites that with a navigate to /dashboard.
  const redirectedRef = React.useRef(false);

  // Once auth state resolves to a signed-in user, leave the login page.
  // A Product Catalog selection made while signed out (architecture §2.4)
  // and a pending MCP OAuth authorization (see McpAuthorizePage) both take
  // priority over the default /dashboard landing — checked in that order,
  // since they are mutually exclusive in practice (a signed-out user isn't
  // both mid-catalog-selection and mid-MCP-connector-approval at once).
  useEffect(() => {
    if (!hydrated || !user || redirectedRef.current) return;
    redirectedRef.current = true;
    const pendingSelection = consumePendingCatalogSelection();
    if (pendingSelection) {
      if (pendingSelection.productId) {
        navigate('/catalog/' + encodeURIComponent(pendingSelection.productId), { replace: true });
        return;
      }
      navigate('/wizard', {
        replace: true,
        state: { initialStep: 3, templateId: pendingSelection.templateId, occasionType: pendingSelection.occasionType },
      });
      return;
    }
    const pendingMcpAuthorize = sessionStorage.getItem('mcp_authorize_return_to');
    if (pendingMcpAuthorize) {
      sessionStorage.removeItem('mcp_authorize_return_to');
      navigate(pendingMcpAuthorize, { replace: true });
      return;
    }
    if (isSafeReturnTo(returnTo)) {
      navigate(returnTo, { replace: true });
      return;
    }
    navigate('/dashboard', { replace: true });
  }, [hydrated, user, navigate, returnTo]);

  const isSignup = initialTab === 'signup';
  const copy = useMemo(
    () =>
      isSignup
        ? {
            eyebrow: 'Start designing',
            title: 'Create your InviteCloud account',
            body: 'Launch polished invitation websites with templates, media, RSVPs, and publishing built in.',
          }
        : {
            eyebrow: 'Welcome back',
            title: 'Sign in to your invitation studio',
            body: 'Create, publish, and manage beautiful guest experiences from one secure dashboard.',
          },
    [isSignup],
  );

  const handle = async (id: ProviderId, fn: () => Promise<AuthResult>) => {
    setError('');
    setPending(id);
    const result = await fn();
    // On success, check attribution. Terms acceptance is handled post-login
    // by the global TermsAgreementModal (rendered from AuthProvider), gated
    // on the authenticated user's server-computed termsAcceptanceRequired —
    // not by anything here. See docs/features/terms-agreement-versioning/.
    if (result.ok) {
      try {
        const attrRaw = localStorage.getItem('invitecloud.attribution.v1');
        if (attrRaw) {
          const attr = JSON.parse(attrRaw);
          const time = new Date(attr.timestamp).getTime();
          if (Date.now() - time < 7 * 24 * 60 * 60 * 1000 && attr.referredByInvitationSlug) {
            import('@/src/infrastructure/lib/analytics').then(({ trackEvent }) => {
              trackEvent('RSVP_CONVERTED_TO_USER', 'viral_conversion', attr.referredByInvitationSlug);
            });
            localStorage.removeItem('invitecloud.attribution.v1');
          }
        }
      } catch (_) {}
      return;
    }
    setError(result.error ?? 'Sign-in failed. Please try again.');
    setPending(null);
  };

  const busy = pending !== null;

  return (
    <div className="force-light relative min-h-screen overflow-hidden bg-surface text-on-surface">
      <AnimatedGradientBackground />
      <div className="relative z-10 grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        {/* ── Left hero panel ───────────────────────────────────────────── */}
        <section className="relative hidden overflow-hidden border-r border-white/45 px-12 py-10 lg:flex lg:flex-col lg:justify-between">
          <div className="relative z-10">
            <BrandLogo variant="full" size="xl" href="/" />
          </div>

          <div className="relative z-10 max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28 }}
              className="space-y-6"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white/65 px-4 py-2 text-sm font-bold text-brand-deep shadow-sm backdrop-blur">
                <Sparkles className="h-4 w-4" />
                {copy.eyebrow}
              </span>
              <div className="space-y-4">
                <h1 className="max-w-[12ch] text-6xl font-bold leading-[0.95] tracking-normal text-on-surface">
                  {copy.title}
                </h1>
                <p className="max-w-md text-lg font-medium leading-8 text-on-surface-variant">
                  {copy.body}
                </p>
              </div>
            </motion.div>
          </div>

          <div className="relative z-10 grid grid-cols-3 gap-3">
            {authHighlights.map(([label, Icon]) => (
              <div key={label} className="rounded-2xl border border-white/70 bg-white/60 p-4 shadow-sm backdrop-blur">
                <Icon className="mb-3 h-5 w-5 text-primary" />
                <p className="text-sm font-bold text-on-surface">{label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Auth card ─────────────────────────────────────────────────── */}
        <main className="flex items-center justify-center px-5 py-10 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="mb-8 flex justify-center lg:hidden">
              <BrandLogo variant="full" size="lg" href="/" />
            </div>

            <div className="rounded-[2rem] border border-white/70 bg-white/80 p-6 shadow-2xl shadow-brand-deep/10 backdrop-blur sm:p-8">
              <div className="mb-7 space-y-2 text-center">
                <p className="text-sm font-bold uppercase tracking-wider text-primary">
                  {copy.eyebrow}
                </p>
                <h2 className="text-3xl font-bold tracking-normal text-on-surface">
                  Continue to InviteCloud
                </h2>
                <p className="text-sm font-medium leading-6 text-on-surface-variant">
                  Sign in with a social account — no passwords to remember.
                </p>
              </div>

              {returnTo?.startsWith('/wizard') && (
                <div
                  data-testid="wizard-redirect-banner"
                  className="mb-5 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-center text-xs font-semibold text-primary"
                >
                  Sign in to start your design
                </div>
              )}

              <div className="flex flex-col gap-3">
                <ProviderButton
                  icon={<GoogleIcon />}
                  label="Continue with Google"
                  loading={pending === 'google'}
                  disabled={busy}
                  onClick={() => handle('google', loginWithGoogle)}
                />
                <ProviderButton
                  icon={<FacebookIcon />}
                  label="Continue with Facebook"
                  loading={pending === 'facebook'}
                  disabled={busy}
                  onClick={() => handle('facebook', loginWithFacebook)}
                />
                <ProviderButton
                  icon={<MicrosoftIcon />}
                  label="Continue with Microsoft"
                  loading={pending === 'microsoft'}
                  disabled={busy}
                  onClick={() => handle('microsoft', loginWithMicrosoft)}
                />
              </div>

              {error && (
                <p
                  role="alert"
                  className="mt-5 rounded-2xl border border-status-failed/20 bg-status-failed/10 px-4 py-3 text-sm font-semibold text-status-failed"
                >
                  {error}
                </p>
              )}

              <p className="mt-6 text-center text-xs font-medium leading-5 text-on-surface-variant">
                By continuing you agree to the InviteCloud{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline underline-offset-2 hover:text-primary-dark"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline underline-offset-2 hover:text-primary-dark"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
}

export default Login;
